import { useEffect, useId, useState, type FormEvent } from "react";
import { CheckCircle2, Ticket } from "lucide-react";

import { WalletBetaDisclaimer } from "@/components/ai-wallet/WalletBetaDisclaimer";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  readFirstLoginPromoStatus,
  redeemFirstLoginPromo,
  skipFirstLoginPromo,
} from "@/lib/promo-code";

type WalletEntryStage = "checking" | "question" | "code" | "success" | "done";

const WALLET_FREE_SUCCESS = "Congrats, you’ve now got the wallet for free.";

export function WalletEntryPrompts({ userID }: { userID: string }) {
  const promoCodeID = useId();
  const [stage, setStage] = useState<WalletEntryStage>("checking");
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [successMessage, setSuccessMessage] = useState(WALLET_FREE_SUCCESS);

  useEffect(() => {
    let cancelled = false;
    setStage("checking");
    setCode("");
    setError("");
    setBusy(false);
    setSuccessMessage(WALLET_FREE_SUCCESS);

    void readFirstLoginPromoStatus()
      .then((status) => {
        if (!cancelled) {
          setStage(status.prompt_required ? "question" : "done");
        }
      })
      .catch(() => {
        if (!cancelled) setStage("done");
      });

    return () => {
      cancelled = true;
    };
  }, [userID]);

  const skipPrompt = async () => {
    if (busy) return;
    setBusy(true);
    setError("");
    try {
      await skipFirstLoginPromo();
    } catch {
      // This optional prompt must never block access to the wallet.
    } finally {
      setStage("done");
      setBusy(false);
    }
  };

  const redeemPromo = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (busy) return;
    if (!code.trim()) {
      setError("Enter your promo code.");
      return;
    }

    setBusy(true);
    setError("");
    try {
      const result = await redeemFirstLoginPromo(code);
      if (!result.redeemed || !result.wallet_free) {
        setError("That promo code isn’t registered.");
        return;
      }
      setSuccessMessage(result.success_message || WALLET_FREE_SUCCESS);
      setStage("success");
    } catch (requestError) {
      const detail =
        requestError &&
        typeof requestError === "object" &&
        "detail" in requestError &&
        typeof requestError.detail === "string"
          ? requestError.detail
          : "";
      setError(detail || "We couldn’t check that code. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  if (stage === "checking") return null;
  if (stage === "done") return <WalletBetaDisclaimer userID={userID} />;

  return (
    <AlertDialog open>
      <AlertDialogContent className="w-[calc(100vw-2rem)] max-w-[460px] gap-0 overflow-hidden rounded-[28px] border border-black/10 bg-[#f8f7f3] p-0 text-[#0e0c08] shadow-[0_28px_90px_rgba(0,0,0,0.28)]">
        <div className="border-b border-black/[0.07] px-6 pb-5 pt-6 sm:px-7 sm:pt-7">
          <div className="mb-5 flex items-center gap-3">
            <span className="flex h-10 w-10 items-center justify-center rounded-2xl bg-emerald-100 text-emerald-800">
              {stage === "success" ? (
                <CheckCircle2 aria-hidden="true" className="h-5 w-5" />
              ) : (
                <Ticket aria-hidden="true" className="h-5 w-5" />
              )}
            </span>
            <span className="rounded-full border border-emerald-700/20 bg-emerald-100 px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.18em] text-emerald-900">
              Welcome
            </span>
          </div>

          <AlertDialogHeader className="space-y-3 text-left">
            <AlertDialogTitle className="text-2xl font-bold tracking-[-0.025em]">
              {stage === "success"
                ? "Promo code applied"
                : stage === "code"
                  ? "Enter your promo code"
                  : "Do you have a promo code?"}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-[14px] leading-6 text-black/60">
              {stage === "success"
                ? successMessage
                : stage === "code"
                  ? "We’ll check it against our registered promo codes."
                  : "If you have one, you can apply it before opening your wallet."}
            </AlertDialogDescription>
          </AlertDialogHeader>
        </div>

        {stage === "question" ? (
          <div className="px-6 py-5 sm:px-7 sm:py-6">
            <AlertDialogFooter className="grid grid-cols-2 gap-3 sm:grid-cols-2">
              <button
                type="button"
                disabled={busy}
                onClick={() => void skipPrompt()}
                className="h-11 rounded-xl border border-black/10 bg-white px-5 text-[13px] font-semibold text-[#0e0c08] transition-colors hover:bg-black/[0.04] disabled:opacity-50"
              >
                {busy ? "Saving…" : "No"}
              </button>
              <button
                type="button"
                disabled={busy}
                onClick={() => setStage("code")}
                className="h-11 rounded-xl bg-[#0e0c08] px-5 text-[13px] font-semibold text-white transition-colors hover:bg-[#25211b] disabled:opacity-50"
              >
                Yes
              </button>
            </AlertDialogFooter>
          </div>
        ) : null}

        {stage === "code" ? (
          <form onSubmit={(event) => void redeemPromo(event)} className="space-y-5 px-6 py-5 sm:px-7 sm:py-6">
            <div className="space-y-2">
              <label htmlFor={promoCodeID} className="text-[12px] font-semibold text-black/70">
                Promo code
              </label>
              <input
                id={promoCodeID}
                value={code}
                onChange={(event) => {
                  setCode(event.target.value);
                  if (error) setError("");
                }}
                autoComplete="off"
                autoCapitalize="characters"
                spellCheck={false}
                autoFocus
                aria-invalid={error ? true : undefined}
                aria-describedby={error ? `${promoCodeID}-error` : undefined}
                className="h-11 w-full rounded-xl border border-black/15 bg-white px-3.5 text-[14px] font-medium uppercase tracking-[0.08em] outline-none transition-shadow focus:border-black/30 focus:ring-2 focus:ring-black/10"
              />
              {error ? (
                <p id={`${promoCodeID}-error`} role="alert" className="text-[12px] font-medium text-red-700">
                  {error}
                </p>
              ) : null}
            </div>
            <AlertDialogFooter className="grid grid-cols-2 gap-3 sm:grid-cols-2">
              <button
                type="button"
                disabled={busy}
                onClick={() => {
                  setError("");
                  setStage("question");
                }}
                className="h-11 rounded-xl border border-black/10 bg-white px-5 text-[13px] font-semibold text-[#0e0c08] transition-colors hover:bg-black/[0.04] disabled:opacity-50"
              >
                Back
              </button>
              <button
                type="submit"
                disabled={busy}
                className="h-11 rounded-xl bg-[#0e0c08] px-5 text-[13px] font-semibold text-white transition-colors hover:bg-[#25211b] disabled:opacity-50"
              >
                {busy ? "Checking…" : "Apply code"}
              </button>
            </AlertDialogFooter>
          </form>
        ) : null}

        {stage === "success" ? (
          <div className="px-6 py-5 sm:px-7 sm:py-6">
            <AlertDialogFooter>
              <button
                type="button"
                onClick={() => setStage("done")}
                className="h-11 w-full rounded-xl bg-[#0e0c08] px-5 text-[13px] font-semibold text-white transition-colors hover:bg-[#25211b]"
              >
                Continue to wallet
              </button>
            </AlertDialogFooter>
          </div>
        ) : null}
      </AlertDialogContent>
    </AlertDialog>
  );
}
